/**
 * The hero illustration: a wedding at a cathedral window.
 *
 * Redrawn from the reference design, which built it out of some sixty
 * absolutely-positioned divs and two `clip-path: path()` rules. That cannot be
 * made responsive -- `path()` takes no percentages, so the whole thing is
 * pinned to 256x372 pixels and simply overflows a phone. One SVG with a
 * viewBox says the same thing, scales to any width, and costs one element.
 *
 * Everything that moves is driven from landing.css so that one
 * `prefers-reduced-motion` block can stop all of it.
 */

/**
 * A bloom: a pale centre with coloured petals around it.
 *
 * The petals OVERLAP the centre -- offset 4.5 against a radius of 3.4 -- rather
 * than ringing it at a distance, which at this size reads as a plus sign. That
 * is what the first version drew.
 */
function Flower({ x, y, petal, s = 1 }: { x: number; y: number; petal: string; s?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      {[0, 72, 144, 216, 288].map((a) => (
        <circle
          key={a}
          cx={(4.5 * Math.sin((a * Math.PI) / 180)).toFixed(2)}
          cy={(-4.5 * Math.cos((a * Math.PI) / 180)).toFixed(2)}
          r="3.4"
          fill={petal}
        />
      ))}
      <circle cx="0" cy="0" r="2.4" fill="#F6E7C8" />
    </g>
  );
}

/** One leaf off a stem. `a` is the angle it sits at, in degrees. */
function Leaf({ x, y, a, fill }: { x: number; y: number; a: number; fill: string }) {
  return (
    <path
      d="M 0 0 C 5 -4 11 -3 12 0 C 11 3 5 4 0 0 Z"
      fill={fill}
      transform={`translate(${x} ${y}) rotate(${a})`}
    />
  );
}

const HEART =
  'M 0 -2 C -1.7 -5 -6 -4 -6 -0.6 C -6 2.4 -2.6 4.6 0 6 C 2.6 4.6 6 2.4 6 -0.6 C 6 -4 1.7 -5 0 -2 Z';

export function CathedralWindow() {
  return (
    <svg
      className="window"
      viewBox="0 0 256 372"
      role="img"
      aria-label="A bride and groom before a cathedral window"
    >
      <defs>
        {/* The glass, as a clip -- so nothing inside the window escapes it. */}
        <clipPath id="glass">
          <path d="M 12 366 L 12 152 C 12 92 82 45 128 32 C 174 45 244 92 244 152 L 244 366 Z" />
        </clipPath>
        <linearGradient id="glassFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0C5C8C" />
          <stop offset="1" stopColor="#06395A" />
        </linearGradient>
        <radialGradient id="crossGlow">
          <stop offset="0" stopColor="#A6192E" stopOpacity="0.5" />
          <stop offset="0.66" stopColor="#A6192E" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="floorGlow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F4ECDD" stopOpacity="0" />
          <stop offset="1" stopColor="#F4ECDD" stopOpacity="0.18" />
        </linearGradient>
        <linearGradient id="veil" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.42" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0.06" />
        </linearGradient>
      </defs>

      {/* The frame: a pointed arch, in the red of the CREC mark. */}
      <path
        d="M 8 372 L 8 150 C 8 88 80 40 128 25 C 176 40 248 88 248 150 L 248 372 Z"
        fill="#A6192E"
      />

      <g clipPath="url(#glass)">
        <rect x="0" y="0" width="256" height="372" fill="url(#glassFill)" />

        {/* Cross, haloed. */}
        <ellipse className="glow" cx="128" cy="84" rx="54" ry="49" fill="url(#crossGlow)" />
        <g className="cross">
          <rect x="123.5" y="54" width="9" height="60" rx="3" fill="#F4E7C9" />
          <rect x="108" y="70" width="40" height="9" rx="3" fill="#F4E7C9" />
        </g>

        {/*
          Hearts rising through the light.

          The GROUP carries the position and the PATH carries the animation. A
          CSS transform replaces the `transform` attribute outright rather than
          composing with it, so animating a positioned element would fling it to
          the origin -- which is a five-minute mystery every single time.
        */}
        <g transform="translate(104 210)">
          <path className="heart heart1" d={HEART} fill="#F2C9CF" />
        </g>
        <g transform="translate(154 205)">
          <path className="heart heart2" d={HEART} fill="#F2C9CF" />
        </g>
        <g transform="translate(129 199)">
          <path className="heart heart3" d={HEART} fill="#FBEFE0" />
        </g>

        <rect x="0" y="336" width="256" height="30" fill="url(#floorGlow)" />

        {/* The couple. */}
        <g className="bride">
          {/* The veil, falling from behind her head and trailing to the floor. */}
          <path
            d="M 101 230 C 80 230 76 250 79 268 L 63 342 L 139 342 L 123 268 C 126 250 122 230 101 230 Z"
            fill="url(#veil)"
          />
          <path d="M 74 342 L 128 342 L 101 264 Z" fill="#F4ECDD" />
          <circle cx="101" cy="248" r="14" fill="#12324A" />
        </g>
        <g className="groom">
          <rect x="139" y="256" width="44" height="86" rx="14" fill="#081B2C" />
          <circle cx="161" cy="242" r="14" fill="#081B2C" />
        </g>

        {/* Climbing flowers, up both jambs. */}
        <g>
          <path
            d="M 20 366 C 14 320 30 290 24 250 C 20 220 36 200 44 178"
            fill="none"
            stroke="#4E7A4C"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <Leaf x={15} y={341} a={38} fill="#4E7A4C" />
          <Leaf x={43} y={325} a={-28} fill="#5C8A55" />
          <Leaf x={21} y={253} a={40} fill="#4E7A4C" />
          <Leaf x={43} y={209} a={-32} fill="#5C8A55" />
          <Flower x={21} y={331} petal="#A6192E" />
          <Flower x={37} y={339} petal="#F4ECDD" />
          <Flower x={29} y={315} petal="#E68A96" />
          <Flower x={25} y={271} petal="#A6192E" />
          <Flower x={37} y={231} petal="#E68A96" />
          <Flower x={53} y={191} petal="#F4ECDD" s={0.85} />
        </g>
        <g>
          <path
            d="M 236 366 C 242 320 226 290 232 250 C 236 220 220 200 212 178"
            fill="none"
            stroke="#4E7A4C"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <Leaf x={241} y={341} a={142} fill="#4E7A4C" />
          <Leaf x={213} y={325} a={208} fill="#5C8A55" />
          <Leaf x={235} y={253} a={140} fill="#4E7A4C" />
          <Leaf x={213} y={209} a={212} fill="#5C8A55" />
          <Flower x={234} y={331} petal="#A6192E" />
          <Flower x={218} y={339} petal="#F4ECDD" />
          <Flower x={226} y={315} petal="#E68A96" />
          <Flower x={230} y={271} petal="#A6192E" />
          <Flower x={218} y={231} petal="#E68A96" />
          <Flower x={202} y={191} petal="#F4ECDD" s={0.85} />
        </g>
      </g>
    </svg>
  );
}
